import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Analytics } from '@vercel/analytics/react';
import { toast } from 'sonner';
import App from './App';
import { AuthProvider } from './app/AuthProvider';
import { Toaster } from './components/ui/Toaster';
import { supabase } from './lib/supabase';
import { SESSION_EXPIRED_MESSAGE, hasNoActiveSession } from './lib/session';
import './index.css';

// After a deploy, Vite's content-hashed chunk names change, so a tab that
// was opened against the previous build will 404 when it tries to lazily
// import a now-missing chunk (e.g. the ExcelJS-backed spreadsheet module).
// Vite fires `vite:preloadError` in that case — recover by reloading once so
// the browser pulls the fresh index.html + asset names. A short cool-down
// prevents a reload storm if a chunk is genuinely gone, while still letting a
// later deploy trigger its own recovery.
window.addEventListener('vite:preloadError', () => {
  const KEY = 'atlas:last-preload-reload';
  const last = Number(sessionStorage.getItem(KEY) ?? 0);
  if (Date.now() - last < 10_000) return;
  sessionStorage.setItem(KEY, String(Date.now()));
  window.location.reload();
});

// Guard against double-firing when several writes fail at once after a
// session dies — we only want one toast + one sign-out.
let sessionExpiryHandled = false;

const queryClient = new QueryClient({
  // Any mutation (write) that fails purely because the session is gone
  // would otherwise surface as a confusing RLS error ("new row violates
  // row-level security policy"). Catch that centrally: if there's no
  // usable session when a write fails, show a clear re-login prompt and
  // sign the user out so the route guards bounce them to the sign-in page.
  mutationCache: new MutationCache({
    onError: () => {
      void hasNoActiveSession().then((expired) => {
        if (!expired || sessionExpiryHandled) return;
        sessionExpiryHandled = true;
        toast.error(SESSION_EXPIRED_MESSAGE);
        void supabase.auth.signOut().finally(() => {
          sessionExpiryHandled = false;
        });
      });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('Missing #root element in index.html');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <App />
          <Toaster />
          <Analytics />
        </AuthProvider>
      </BrowserRouter>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </StrictMode>,
);
